import { AlertTriangle, FolderOpen } from 'lucide-react'

import type { PromotionHold } from '@/services/ingestion'

import { holdReasons } from './holdReasons'

/**
 * Why a record is not a deal yet, and what releases it — every reason (ENT-12).
 *
 * A record can be held for more than one reason at once (an email that lists
 * several deals and names its deal from a subject-line fragment): the first is
 * drawn as it always was, and each further one under it, so confirming one
 * reason never reads as having cleared them all.
 *
 * `first` replaces the first reason's wording where the record's disposition
 * knows better than the hold (the lender's own draft terms waiting on a deal).
 */
export default function PromotionHoldNotice({
  hold, first, onOpenDeal,
}: {
  hold: PromotionHold
  first?: { title: string; detail: string }
  onOpenDeal?: (loanId: string) => void
}) {
  const reasons = holdReasons(hold)
  return (
    <div
      data-testid="promotion-hold"
      className="flex items-start gap-2 rounded border border-amber-400/25 bg-amber-400/[0.05] px-2.5 py-2"
    >
      <AlertTriangle size={12} className="mt-px flex-shrink-0 text-amber-300" />
      <div className="min-w-0 flex-1">
        {reasons.map((r, i) => (
          <div
            key={r.reason}
            data-testid="promotion-hold-reason"
            className={i === 0 ? '' : 'mt-2 border-t border-amber-400/15 pt-2'}
          >
            <div className="text-[11px] font-medium text-amber-200">
              {i === 0 ? 'Not a deal yet — ' : 'Also held — '}
              {i === 0 && first ? first.title : r.reason}
            </div>
            <div className="text-[10px] text-stone-400">{i === 0 && first ? first.detail : r.detail}</div>
            <div className="mt-0.5 text-[10px] text-stone-300">{r.clearsWhen}</div>
            {r.loanId && onOpenDeal && (
              <button
                type="button"
                onClick={() => onOpenDeal(r.loanId!)}
                className="mt-1 inline-flex items-center gap-1 text-[10px] text-amber-300 underline decoration-dotted hover:text-amber-200"
              >
                <FolderOpen size={10} />
                Open deal {r.loanId}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
